import Chevron from 'src/components/dashboard/components/navigationMenu/components/chevron';
import { formatDate } from 'src/utils/formatDate';
import { formatMoney } from 'src/utils/formatMoney';
import { IReservesObject } from 'src/services/models/reserves.service';
import { useLocalization } from 'src/utils/useLocalization';
import { Dispatch, SetStateAction } from 'react';

import './style.scss';

export interface ITableContents {
    items: IReservesObject[];
    sortType: string;
    sortOrder: boolean;
    setSortType: Dispatch<SetStateAction<string>>;
    setSortOrder: Dispatch<SetStateAction<boolean>>;
    isActivity: boolean;
}

export interface ReservesTableItemData {
    data: IReservesObject;
    isActivity: boolean;
}

export const TableItem: React.FC<ReservesTableItemData> = ({
    data,
    isActivity,
}) => {
    const {
        accountNumber,
        customerName,
        vin,
        transactionType,
        amountFinanced,
        paymentAmount,
        endOfMonth,
    } = data;

    const { reserves } = useLocalization();
    return (
        <div className={`row${isActivity ? '-7' : ''}`}>
            <div className='table-row re-col-1'>
                <span className='list-label'>{reserves.TABLE_ACCOUNT}</span>
                <span>{accountNumber}</span>
            </div>
            <div className='table-row col-2'>
                <span className='list-label'>{reserves.TABLE_CUSTOMER}</span>
                <span>{customerName}</span>
            </div>
            <div className='table-row col-3'>
                <span className='list-label'>{reserves.TABLE_VIN}</span>
                <span>{vin}</span>
            </div>
            <div className='table-row col-4'>
                <span className='list-label'>{reserves.TABLE_TRANSACTION}</span>
                <span>{transactionType}</span>
            </div>
            <div className='table-row col-5'>
                <span className='list-label'>{reserves.TABLE_FINANCE}</span>
                <span>{formatMoney(amountFinanced)}</span>
            </div>
            <div className='table-row col-6'>
                <span className='list-label'>{reserves.TABLE_PAYMENT}</span>
                <span>{formatMoney(paymentAmount)}</span>
            </div>
            {isActivity && (
                <div className='table-row col-7'>
                    <span className='list-label'>{'EOM'}</span>
                    <span>{formatDate(endOfMonth)}</span>
                </div>
            )}
        </div>
    );
};

export const Table: React.FC<ITableContents> = ({
    items,
    sortType,
    sortOrder,
    setSortType,
    setSortOrder,
    isActivity,
}) => {
    let aux = 0;
    const chevronColor = '#363b49';
    const { reserves } = useLocalization();

    /**
     * Function to sort based on the property and sort order
     * @param property
     * @param order
     */
    const applySort = (property: string, order = true) => {
        if (sortType == property) {
            setSortOrder(!sortOrder);
        } else {
            setSortType(property);
            setSortOrder(order);
        }
    };

    /**
     * Function to render sorting buttons on table header
     * @param property
     * @param clickhandler
     * @returns
     */
    const renderTableSort = (property: string, clickhandler: any) => (
        <span className='table-header__order'>
            <span
                onClick={() => {
                    clickhandler(property);
                }}
            >
                <Chevron
                    className={`accordion__icon ${
                        sortOrder && sortType == property
                            ? 'rotate-down'
                            : 'rotate-up'
                    }`}
                    width={10}
                    fill={chevronColor}
                />
            </span>
            {sortType != property ? (
                <span
                    onClick={() => {
                        clickhandler(property, false);
                    }}
                >
                    <Chevron
                        className='accordion__icon rotate-down'
                        width={10}
                        fill={chevronColor}
                    />
                </span>
            ) : null}
        </span>
    );
    return (
        <div className='reserves-table-wrapper'>
            <div
                className={`table${isActivity ? '-7' : ''}`}
                id='reserves-table'
            >
                <div
                    className={`row${
                        isActivity ? '-7' : ''
                    } header-row hidden-in-list`}
                >
                    <div className='table-header re-col-1'>
                        <span className='u-text-truncate'>
                            {reserves.TABLE_ACCOUNT}
                        </span>
                    </div>
                    <div className='table-header col-2'>
                        <span className='u-text-truncate'>
                            {reserves.TABLE_CUSTOMER}
                        </span>
                        {renderTableSort('customerName', applySort)}
                    </div>
                    <div className='table-header col-3'>
                        <span className='u-text-truncate'>
                            {reserves.TABLE_VIN}
                        </span>
                    </div>
                    <div className='table-header col-4'>
                        <span className='u-text-truncate'>
                            {reserves.TABLE_TRANSACTION}
                        </span>
                        {renderTableSort('transactionType', applySort)}
                    </div>
                    <div className='table-header col-5'>
                        <span className='u-text-truncate'>
                            {reserves.TABLE_FINANCE}
                        </span>
                        {renderTableSort('amount', applySort)}
                    </div>
                    <div className='table-header col-6'>
                        <span className='u-text-truncate'>
                            {reserves.TABLE_PAYMENT}
                        </span>
                        {renderTableSort('payment', applySort)}
                    </div>
                    {isActivity && (
                        <div className='table-header col-7'>
                            <span className='u-text-truncate'>{'EOM'}</span>
                            {renderTableSort('enfOfMonth', applySort)}
                        </div>
                    )}
                </div>
                {items.map((item) => (
                    <TableItem
                        data={item}
                        isActivity={isActivity}
                        key={aux++}
                    />
                ))}
            </div>
        </div>
    );
};

export default Table;
